import React, { useState, useEffect } from "react";
import "./TheForm.css";

const badges = [
  "تقديم الاستشارات الفردية من مختصين",
  "تصميم خطط علاجية عملية تناسب احتياجات العميل",
  "تقييم ومتابعة التقدم",
  "تغيير محتوى ودورات خاصّة لذوي الإعاقة",
  "بناء مجتمع داعم ومساند لتواصل بلا أحكام",
];

/* ✅ حالة مبدئية لتفريغ الحقول بسهولة بعد الإرسال */
const initialData = {
  name: "",
  phone: "",
  email: "",
  specialty: "",
  region: "",
  experience: "",
  bio: "",       // اختياري
  agree: false,
};

/* Toast خفيفة بدون مكتبات */
function Toast({ open, message, type = "success", onClose, duration = 2500 }) {
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(onClose, duration);
    return () => clearTimeout(t);
  }, [open, duration, onClose]);

  if (!open) return null;
  return (
    <div className={`toast toast--${type}`} role="status" aria-live="polite" dir="rtl">
      <span className="toast__icon" aria-hidden>{type === "success" ? "✓" : "!"}</span>
      <span className="toast__text">{message}</span>
      <button className="toast__close" onClick={onClose} aria-label="إغلاق">×</button>
    </div>
  );
}

export default function TheForm() {
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ open: false, type: "success", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const experienceOptions = [
    { value: 0, label: "0 - 2" },
    { value: 3, label: "3 - 5" },
    { value: 6, label: "6 - 10" },
    { value: 10, label: "+10" },
  ];

  const update = (e) => {
    const { name, value, type, checked } = e.target;
    setData((d) => ({ ...d, [name]: type === "checkbox" ? checked : value }));
  };

  // قواعد التحقق (بدون bio لأنها اختيارية)
  const rules = {
    name: (v) =>
      !v ? "الاسم مطلوب" : v.trim().length < 2 ? "الاسم قصير جدًا" : "",
    email: (v) =>
      !v
        ? "البريد الإلكتروني مطلوب"
        : !/^\S+@\S+\.\S+$/.test(v)
          ? "بريد إلكتروني غير صالح"
          : "",
    phone: (v) =>
      !v
        ? "رقم الجوال مطلوب"
        : !/^\+?\d[\d\s-]{7,}$/.test(v)
          ? "رقم غير صالح (أدخل أرقامًا فقط مع السماح بالمسافات والشرطات +)"
          : "",
    specialty: (v) => (!v ? "اختر التخصص" : ""),
    region: (v) => (!v ? "اختر المجال/الفئة" : ""),
    experience: (v) => (!v ? "اختر سنوات الخبرة" : ""),
  };

  const validateField = (name, value) =>
    rules[name] ? rules[name](value || "") : "";

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const submit = async (e) => {
    e.preventDefault();

    // تحقّق شامل
    const nextErrors = {};
    Object.keys(rules).forEach((k) => {
      const msg = validateField(k, data[k]);
      if (msg) nextErrors[k] = msg;
    });

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      // التركيز على أول خطأ
      setTimeout(() => {
        const first = document.querySelector('[data-has-error="true"]');
        first?.scrollIntoView({ behavior: "smooth", block: "center" });
        if (first && "focus" in first) first.focus();
      }, 0);
      return;
    }

    // بناء الحمولة بما يتوافق مع CreateDoctorRequestDto في الباكند
    const payload = {
      firstName: data.name,
      whatsappNumber: data.phone,
      email: data.email,
      specialization: data.specialty,
      yearsOfExperience: Number(data.experience || 0),
      category: data.region,
      description: data.bio || "",
    };

    try {
      setIsSubmitting(true);
      const res = await fetch('/api/doctor-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `HTTP ${res.status}`);
      }

      setToast({ open: true, type: "success", message: "تم إرسال طلبك بنجاح 👌" });
      setData(initialData);
      setErrors({});
    } catch (err) {
      setToast({ open: true, type: "error", message: "حدث خطأ أثناء الإرسال. حاول مجددًا." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="apply2" dir="rtl" aria-labelledby="apply-title">
      {/* noValidate لإيقاف رسالة المتصفح القديمة */}
      <form className="apply2__card" noValidate onSubmit={submit}>
        {/* Intro */}
        <header className="apply2__intro">
          <p className="apply2__title" id="apply-title">
            مرحبًا بك، هذا النموذج مخصّص لاستقطاب الأخصائيين والأطباء المعتمدين للانضمام إلى منصة
            استشارات نفسية تُوجّه إليك:
          </p>
          <ul className="apply2__badges" aria-label="مميزات الانضمام">
            {badges.map((t, i) => (
              <li className="badge" key={i}>
                <span className="badge__dot" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="12" fill="#0aa58c" opacity=".15" />
                    <path
                      d="M7 12.5l3.2 3.2L17.5 8.5"
                      stroke="#0aa58c"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {t}
              </li>
            ))}
          </ul>
        </header>

        {/* fields */}
        <div className="grid">
          {/* الاسم */}
          <label className="field">
            <span className="field__label">الاسم الكامل</span>
            <input
              className={`field__input ${errors.name ? "is-invalid" : ""}`}
              name="name"
              value={data.name}
              onChange={update}
              onBlur={handleBlur}
              placeholder="اكتب اسمك"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? "err-name" : undefined}
              data-has-error={errors.name ? "true" : "false"}
            />
            {errors.name && (
              <p className="field__error" id="err-name">
                <span className="field__error-ico">!</span>
                {errors.name}
              </p>
            )}
          </label>

          {/* الجوال */}
          <label className="field">
            <span className="field__label">رقم الواتساب للتواصل</span>
            <input
              className={`field__input ${errors.phone ? "is-invalid" : ""}`}
              type="tel"
              name="phone"
              value={data.phone}
              onChange={update}
              onBlur={handleBlur}
              inputMode="tel"
              placeholder="+965 - 00 0000 0000"
              aria-invalid={!!errors.phone}
              aria-describedby={errors.phone ? "err-phone" : undefined}
              data-has-error={errors.phone ? "true" : "false"}
            />
            {errors.phone && (
              <p className="field__error" id="err-phone">
                <span className="field__error-ico">!</span>
                {errors.phone}
              </p>
            )}
          </label>

          {/* البريد */}
          <label className="field">
            <span className="field__label">البريد الإلكتروني</span>
            <input
              className={`field__input ${errors.email ? "is-invalid" : ""}`}
              type="email"
              name="email"
              value={data.email}
              onChange={update}
              onBlur={handleBlur}
              placeholder="Moh@untitledui.com"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "err-email" : undefined}
              data-has-error={errors.email ? "true" : "false"}
            />
            {errors.email && (
              <p className="field__error" id="err-email">
                <span className="field__error-ico">!</span>
                {errors.email}
              </p>
            )}
          </label>

          {/* التخصص */}
          <label className="field">
            <span className="field__label">التخصص</span>
            <select
              className={`field__input ${errors.specialty ? "is-invalid" : ""}`}
              name="specialty"
              value={data.specialty}
              onChange={update}
              onBlur={handleBlur}
              aria-invalid={!!errors.specialty}
              aria-describedby={errors.specialty ? "err-specialty" : undefined}
              data-has-error={errors.specialty ? "true" : "false"}
            >
              <option value="">اختر التخصص</option>
              <option>أخصائي نفسي</option>
              <option>طبيب نفسي</option>
              <option>إرشاد أسري</option>
              <option>أخرى</option>
            </select>
            {errors.specialty && (
              <p className="field__error" id="err-specialty">
                <span className="field__error-ico">!</span>
                {errors.specialty}
              </p>
            )}
          </label>

          {/* المجال / الفئة */}
          <label className="field">
            <span className="field__label">المجال/الفئة التي تودّ العمل عليها غالبًا</span>
            <select
              className={`field__input ${errors.region ? "is-invalid" : ""}`}
              name="region"
              value={data.region}
              onChange={update}
              onBlur={handleBlur}
              aria-invalid={!!errors.region}
              aria-describedby={errors.region ? "err-region" : undefined}
              data-has-error={errors.region ? "true" : "false"}
            >
              <option value="">مثال: اضطرابات القلق - المكتئبين…</option>
              <option>اضطرابات القلق</option>
              <option>الاكتئاب</option>
              <option>العلاقات الأسرية</option>
              <option>الإدمان</option>
            </select>
            {errors.region && (
              <p className="field__error" id="err-region">
                <span className="field__error-ico">!</span>
                {errors.region}
              </p>
            )}
          </label>

          {/* سنوات الخبرة */}
          <label className="field">
            <span className="field__label">سنوات الخبرة</span>
            <select
              className={`field__input ${errors.experience ? "is-invalid" : ""}`}
              name="experience"
              value={data.experience}
              onChange={update}
              onBlur={handleBlur}
              aria-invalid={!!errors.experience}
              aria-describedby={errors.experience ? "err-experience" : undefined}
              data-has-error={errors.experience ? "true" : "false"}
            >
              <option value="">اختر</option>
              {experienceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {errors.experience && (
              <p className="field__error" id="err-experience">
                <span className="field__error-ico">!</span>
                {errors.experience}
              </p>
            )}
          </label>
        </div>

        {/* المساحة المخصصة (اختياري) */}
        <label className="field">
          <span className="field__label">مساحة مخصصة لك (اختياري)</span>
          <textarea
            className="field__input field__textarea"
            name="bio"
            rows="4"
            placeholder="شاركنا رؤيتك… (اختياري)"
            value={data.bio}
            onChange={update}
          />
        </label>

        <div className="apply2__actions">
          <button type="button" className="btn btn--ghost" onClick={() => window.history.back()}>
            إلغاء
          </button>
          <button type="submit" className="btn btn--primary" disabled={isSubmitting}>
            {isSubmitting ? "جارٍ الإرسال…" : "إرسال الطلب"}
          </button>
        </div>
      </form>

      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        duration={2600}
      />
    </section>
  );
}
